// @flow
import * as React from 'react'
import styled from 'styled-components'
import { APPLE_TEXT } from '../SocialAuth/AppleSignIn/AppleSignIn'
import AppleSignIn from '../SocialAuth/AppleSignIn'
import FacebookSignIn from '../SocialAuth/FacebookSignIn'
import { FACEBOOK_TEXT } from '../SocialAuth/FacebookSignIn/FacebookSignIn'
import GoogleSignIn from '../SocialAuth/GoogleSignIn'
import { GOOGLE_TEXT } from '../SocialAuth/GoogleSignIn/GoogleSignIn'
import { useGeolocation } from '../../../helpers/useGeolocation'

import {
  AuthFormSectionContainer,
  CheckBox,
  colors,
  CTASecondaryWhite,
  ErrorMessage,
  ErrorMessageContainer,
  Form,
  FormSmallText,
  H3,
  P16,
  StyledInput
} from '@components/shared'
import { withRouter } from 'next/router'
import { connect } from 'react-redux'
import { signIn, signUp } from '../../../actions/userActions'
import type { AppStateType } from '../../../reducers/appstate'
import type { Dispatch } from 'redux'
import PasswordReset from '@components/auth/PasswordReset'
import { config } from 'react-awesome-styled-grid'
import { isEmailValid } from '@helpers/authHelpers'
import ThemeContext from '@components/shared/ThemeContext'

export type PropsType = {
  loginFormErrorText?: string,
  loginFormErrorCode?: string,
  loginRequestInProgress: boolean,
  showHover: boolean,
  page?: 'signUp' | 'signIn',
  featureAppleAuth: boolean,
  featureFacebookAuth: boolean,
  featureGoogleAuth: boolean,
  theme: 'sand' | 'white',
  subHeader?: string,
  isCenteredAligned: boolean,
  isOutlineStyle: boolean,
  isOrangeBackground: boolean,
  isSocialPayButtonsFirst: boolean,
  createAccountHeader?: string
}
export type DispatchPropsType = {
  signUp: (email: string, password: string) => void,
  signIn: (email: string, password: string) => void
}

export const getMessageFromError = (
  code: string
): { message: string, type: string } => {
  let message
  let type
  const errorMessageMap = {
    'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead.',
    'auth/invalid-email': "There's a typo in your email address",
    'auth/user-not-found': "We didn't find a user with that email address",
    'auth/wrong-password': 'Incorrect password ',
    'auth/too-many-requests':
      'Too many sign in attempts. Please try again later.',
    'auth/account-exists-with-different-credential':
      'An account already exists with the same email address but different sign-in credentials. Sign in with the provider associated with this email.'
  }
  const errorMessage = errorMessageMap[code]
  if (!code || typeof code !== 'string') {
    message = 'Something went wrong'
    type = 'email'
  } else if (code.includes('password')) {
    message = errorMessage || 'Invalid password'
    type = 'password'
  } else if (errorMessage) {
    message = errorMessage
    type = 'email'
  } else if (code.includes('email')) {
    message = 'Invalid email'
    type = 'email'
  } else {
    message = 'Something went wrong'
    type = 'email'
  }
  return {
    message,
    type
  }
}

export function AuthFormContainer (
  props: PropsType & DispatchPropsType
): React.Node {
  const {
    loginFormErrorCode,
    loginFormErrorText,
    loginRequestInProgress,
    showHover,
    signUp,
    signIn,
    page,
    isOrangeBackground,
    isSocialPayButtonsFirst,
    createAccountHeader
  } = props
  let theme = props.theme || 'sand'
  const contextTheme = React.useContext(ThemeContext)
  const { countryCode, isLoading } = useGeolocation()
  const [isMexico, setIsMexico] = React.useState(false)

  React.useEffect((): void => {
    console.log(countryCode, isLoading)
    if (!isLoading && ['MX', 'BO'].includes(countryCode)) {
      setIsMexico(true)
    }
  }, [countryCode, isLoading])

  if (contextTheme) {
    theme = contextTheme
  }

  const [isCreate, setIsCreate] = React.useState()
  const [email, setEmail] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [ageIsVerified, setAgeIsVerified] = React.useState<boolean>(false)

  const [
    [errorMessageEmail, emailValid],
    setEmailErrorStatus
  ] = React.useState([undefined, null])
  const [
    [errorMessagePassword, passwordValid],
    setPasswordErrorStatus
  ] = React.useState([null, null])
  const [[errorMessageAge, ageValid], setAgeStatus] = React.useState([
    null,
    true
  ])
  const [submitted, setSubmitted] = React.useState(false)
  const [isPasswordResetOpen, setIsPasswordResetOpen] = React.useState<boolean>(
    false
  )

  const isSubmitButtonDisabled = !!submitted || email === '' || (!isPasswordResetOpen && password === '')

  React.useEffect((): void => {
    if (page && page === 'signUp') {
      setIsCreate(true)
    } else if (page && page === 'signIn') {
      setIsCreate(false)
    } else {
      setIsCreate(true)
    }
  }, [])

  React.useEffect((): void => {
    if (loginFormErrorText && loginFormErrorCode) {
      const errorForTemplate = getMessageFromError(loginFormErrorCode)
      if (errorForTemplate.type === 'email') {
        setEmailErrorStatus([errorForTemplate.message, false])
      } else {
        setPasswordErrorStatus([errorForTemplate.message, false])
      }
      setSubmitted(false)
    }
  }, [loginFormErrorCode, loginFormErrorText, loginRequestInProgress])

  const socialAuthAgeCheck = (callback: (params?: any) => void): void => {
    if (isCreate && !ageIsVerified) {
      setAgeStatus(['Required: You must verify your age before sign up', false])
      return
    }
    callback()
  }

  const login = (e: any): void => {
    e.preventDefault()
    if (submitted) {
      return
    }

    if (!isEmailValid(email)) {
      setEmailErrorStatus(['Please enter a valid email', false])
      setSubmitted(false)
      return
    }
    if (isCreate && password.length < 6) {
      setPasswordErrorStatus([
        'Password must be at least 6 characters long',
        false
      ])
      setSubmitted(false)
      return
    }
    if (isCreate && !ageIsVerified) {
      setAgeStatus(['Required: You must verify your age before sign up', false])
      setSubmitted(false)
      return
    }
    if (!password) {
      setPasswordErrorStatus(['Please enter a password', false])
      setSubmitted(false)
      return
    }
    if (isCreate) {
      setSubmitted(true)
      signUp(email, password)
    } else {
      setSubmitted(true)
      signIn(email, password)
    }
  }

  const checkEmail = (e: any): void => {
    const newEmail = e.target.value.replace(/\s+/g, '')
    setEmail(newEmail)
    setEmailErrorStatus([undefined, true])
  }
  const checkPassword = (e: any): void => {
    setPassword(e.target.value)
    setPasswordErrorStatus([undefined, true])
  }
  const updateAgeVerification = (): void => {
    setAgeIsVerified(!ageIsVerified)
    setAgeStatus([undefined, true])
  }
  const toggleCreate = (e: any): void => {
    e.preventDefault()
    setEmailErrorStatus([undefined, true])
    setPasswordErrorStatus([undefined, true])
    setAgeStatus([undefined, true])
    setIsCreate(!isCreate)
  }

  const togglePasswordReset = (e: any): void => {
    e.preventDefault()
    setEmailErrorStatus([undefined, true])
    setPasswordErrorStatus([undefined, true])
    setAgeStatus([undefined, true])
    setIsCreate(false)
    setIsPasswordResetOpen(true)
  }

  const emailInvalid = !!(!emailValid && errorMessageEmail)
  const passwordInvalid = !passwordValid && errorMessagePassword
  const buttonText =
    loginRequestInProgress || submitted
      ? 'LOGGING IN'
      : isCreate
        ? 'Create Account'
        : 'Sign In'
  const header = isCreate
    ? (createAccountHeader || 'Create an account')
    : isPasswordResetOpen
      ? 'Reset password'
      : 'Welcome back'

  const renderSocialAuthButtons = (): React.Node => {
    return (
      <>
        {props.featureAppleAuth && (
          <AppleSignIn
            type={isCreate ? APPLE_TEXT.continue : APPLE_TEXT.signIn}
            disable={loginRequestInProgress}
            socialAuthAgeCheck={socialAuthAgeCheck}
            isOutlineStyle={props.isOutlineStyle}
            isSignUp={isCreate}
          />
        )}
        {props.featureGoogleAuth && (
          <GoogleSignIn
            type={isCreate ? GOOGLE_TEXT.continue : GOOGLE_TEXT.signIn}
            disable={loginRequestInProgress}
            socialAuthAgeCheck={socialAuthAgeCheck}
            isOutlineStyle={props.isOutlineStyle}
            isSignUp={isCreate}
          />
        )}
        {props.featureFacebookAuth && (
          <FacebookSignIn
            type={isCreate ? FACEBOOK_TEXT.continue : FACEBOOK_TEXT.signIn}
            disable={loginRequestInProgress}
            socialAuthAgeCheck={socialAuthAgeCheck}
            isOutlineStyle={props.isOutlineStyle}
            isSignUp={isCreate}
          />
        )}
      </>
    )
  }

  const renderCheckBox = (): React.Node => {
    return (
      <CheckBox
        checked={ageIsVerified}
        onChange={updateAgeVerification}
        className='tc-authform-checkbox'
        error={!ageValid}
        theme={'white'}
        text={
          <FormSmallText
            valid={ageValid}
            id='age-verification'
            textAlign='left'
            fontSize='12px'
            color={theme === 'sand' ? colors.sand : colors.white}
          >
            By checking this box, I certify I am at least 18 years old.
          </FormSmallText>
        }
        errorContainer={
          !ageValid && (
            <ErrorMessageContainer minHeight='18px'>
              <ErrorMessage color={colors.tintOrange} showError={!ageValid}>
                {errorMessageAge}
              </ErrorMessage>
            </ErrorMessageContainer>
          )
        }
      />
    )
  }
  return (
    <AuthFormSectionContainer className='AuthFormSectionContainer'>
      <HeaderContainer isCenteredAligned={props.isCenteredAligned}>
        <H3
          color={theme === 'sand' ? colors.sand : colors.white}
          textAlign={props.isCenteredAligned ? 'center' : 'left'}
        >
          {header}
        </H3>
      </HeaderContainer>

      {props.subHeader && isCreate && (
        <P16
          color={theme === 'sand' ? colors.sand : colors.white}
          textAlign={props.isCenteredAligned ? 'center' : 'left'}
          margin='1em 0'
        >
          {props.subHeader}
        </P16>
      )}
      <AuthBody>
        <FormContainer>
          {isSocialPayButtonsFirst && isCreate && renderCheckBox()}
          {isSocialPayButtonsFirst && !isPasswordResetOpen && (
            <>
              {renderSocialAuthButtons()}
              <ThinLine />
            </>
          )}
          {isPasswordResetOpen && <PasswordReset theme={theme} ctaColor={colors.orange} />}
          {!isPasswordResetOpen && (
            <>
              <Form
                onSubmit={login}
                styles={'margin-top: 15px;'}
                autoComplete='on'
              >
                {!isMexico && <StyledInput
                  name='email'
                  type='email'
                  placeholder='Email'
                  className='tc-authform-email'
                  value={email}
                  valid={!emailInvalid}
                  onChange={checkEmail}
                  borderColor={theme === 'sand' ? colors.sand : colors.white70}
                  placeholderColor={theme === 'sand' ? colors.sand : colors.white70}
                  color={theme === 'sand' ? colors.sand : colors.white}
                  height={'40px'}
                  styles={{
                    width: '100%',
                    lineHeight: '40px'
                  }}
                  isError={!!errorMessageEmail}
                />}
                <ErrorMessageContainer minHeight='18px'>
                  <ErrorMessage color={colors.white} showError={emailInvalid}>
                    {errorMessageEmail}
                  </ErrorMessage>
                </ErrorMessageContainer>

                {!isMexico && <StyledInput
                  name='password'
                  type='password'
                  placeholder='Password'
                  className='tc-authform-password'
                  value={password}
                  valid={!passwordInvalid}
                  onChange={checkPassword}
                  borderColor={theme === 'sand' ? colors.sand : colors.white70}
                  placeholderColor={theme === 'sand' ? colors.sand : colors.white70}
                  color={theme === 'sand' ? colors.sand : colors.white}
                  height={'40px'}
                  styles={{
                    width: '100%',
                    lineHeight: '40px'
                  }}
                  isError={!!errorMessagePassword}
                />}

                <ErrorMessageContainer minHeight='18px'>
                  <ErrorMessage
                    color={colors.white}
                    showError={passwordInvalid}
                  >
                    {errorMessagePassword}
                  </ErrorMessage>
                </ErrorMessageContainer>

                {isCreate
                  ? (
                      !isSocialPayButtonsFirst && renderCheckBox()
                    )
                  : (
                  <ForgotPasswordText
                    color={theme === 'sand' ? colors.sand : colors.white}
                    className='forgot-password-text'
                    textAlign={'left'}
                    showHover={showHover}
                  >
                    Forgot your password?{' '}
                    <a
                      style={{ color: 'inherit' }}
                      href='#'
                      onClick={togglePasswordReset}
                    >
                      Click here to reset
                    </a>
                  </ForgotPasswordText>
                    )}

                {!isMexico && <SubmitButton
                  className='tc-authform-submit'
                  onClick={login}
                  isOrangeBackground={isOrangeBackground}
                  disabled={isSubmitButtonDisabled}
                >
                  {buttonText}
                </SubmitButton>}
              </Form>

              {!isSocialPayButtonsFirst && renderSocialAuthButtons()}

              <ToggleSignInSignUpButtonContainer>
                <ToggleSignInSignUpButton
                  className='tc-authform-toggle-create'
                  onClick={toggleCreate}
                  onKeyDown={(event: any): void => {
                    if (event.keyCode === 13) {
                      toggleCreate(event)
                    }
                  }}
                  role='button'
                  aria-label={!isCreate ? 'Sign up' : 'Sign in'}
                  tabIndex='0'
                  showHover={showHover}
                  color={theme === 'pink' ? colors.white : colors.sand}
                >
                  {!isCreate
                    ? (
                    <p>
                      New to Dipsea?{' '}
                      <b
                        style={{
                          textDecoration: 'underline',
                          color: theme === 'pink'
                            ? colors.white
                            : colors.tintOrange
                        }}
                      >
                        Sign up
                      </b>
                    </p>
                      )
                    : (
                    <p>
                      Already have an account?{' '}
                      <b
                        style={{
                          textDecoration: 'underline',
                          color: theme === 'pink'
                            ? colors.white
                            : colors.tintOrange
                        }}
                      >
                        Sign in
                      </b>
                    </p>
                      )}
                </ToggleSignInSignUpButton>
              </ToggleSignInSignUpButtonContainer>

              <TermsText color={theme === 'sand' ? colors.sand : colors.white}>
                {`By continuing, you agree to  Dipsea's `}
                <a
                  style={{
                    color: theme === 'pink' ? colors.white : colors.tintOrange
                  }}
                  target='_blank'
                  rel='noopener noreferrer'
                  href='https://www.dipseastories.com/terms/dark'
                >
                  Terms & Conditions
                </a>
                {' and '}
                <a
                  style={{
                    color: theme === 'pink' ? colors.white : colors.tintOrange
                  }}
                  target='_blank'
                  rel='noopener noreferrer'
                  href='https://www.dipseastories.com/privacypolicy/dark'
                >
                  Privacy Policy
                </a>
              </TermsText>
            </>
          )}
        </FormContainer>
      </AuthBody>
    </AuthFormSectionContainer>
  )
}

const SubmitButton = styled(CTASecondaryWhite)`
  width: 100%; 
`
const ThinLine = styled.hr`
  margin: 22px 0 24px;
  border: 0.5px solid ${colors.white25};
`
const FormContainer = styled.div`
  margin: 0 auto;
  input:focus,
  a:focus,
  button:focus {
    outline: none;
  }
`
const AuthBody = styled.div`
  margin: 0 20px;
  padding: 0;
  width: 100%;
`
const TermsText = styled.p`
  color: ${(props: any): string => props.color || colors.sand};
  font-family: 'Good Sans Regular';
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  line-height: 1.5em;
  margin: 0;
  padding: 0;
  text-align: center;
  a {
    border-radius: 5px;
    :hover {
      opacity: 0.5;
    }
  }
`
const ToggleSignInSignUpButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 22px;
  margin-bottom: 12px;
`
const ToggleSignInSignUpButton = styled.button`
  align-self: center;
  background: none;
  border: none;
  border-radius: 5px;
  color: ${(props: any): string => props.color || colors.sand};
  cursor: pointer;
  font-family: 'Good Sans Regular';
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  line-height: 1.5em;
  margin: 0 auto;
  padding: 0;
  p {
    margin: 0;
  }
  b:hover {
    opacity: 0.5;
  }
`
const ForgotPasswordText = styled.p`
  font-family: 'Good Sans Regular';
  font-size: 14px;
  line-height: 21px;
  margin: 0 0 24px 0;
  color: ${(props: any): string => props.color || colors.sand};

  a {
    border-radius: 5px;
    :hover {
      opacity: 0.5;
    }
  }
`
const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  ${(props: any): string => config(props).media.lg`
    justify-content: ${(props: any): string =>
      props.isCenteredAligned ? 'center' : 'flex-start'};
  `}
`

type OwnPropsType = {
  router: { query: { page?: 'signIn' | 'signUp' } },
  theme: 'sand' | 'white',
  subHeader?: string,
  isCenteredAligned?: boolean,
  isOutlineStyle?: boolean,
  isOrangeBackground?: boolean,
  isSocialPayButtonsFirst?: boolean,
  page?: 'signIn' | 'signUp'
}

const mapStateToProps = (state: AppStateType, ownProps: OwnPropsType): any => {
  let featureAppleAuth = state.featureAppleAuth
  let featureFacebookAuth = state.featureFacebookAuth
  let featureGoogleAuth = state.featureGoogleAuth
  let page = ownProps.router.query.page || ownProps.page

  if (typeof window !== 'undefined') {
    const search = new URLSearchParams(window.location.search)
    const oAuthStatus = search.get('o_auth')
    if (oAuthStatus === 'no') {
      featureAppleAuth = false
      featureFacebookAuth = false
      featureGoogleAuth = false
    }

    const signIn = search.get('signin')
    if (signIn === 'yes') {
      page = 'signIn'
    }
  }

  return ({
    loginRequestInProgress: state.loginRequestInProgress,
    loginFormErrorText: state.loginFormErrorText,
    loginFormErrorCode: state.loginFormErrorCode,
    showHover: state.hasMouse,
    page,
    featureAppleAuth,
    featureFacebookAuth,
    featureGoogleAuth,
    theme: ownProps.theme,
    subHeader: ownProps.subHeader,
    isCenteredAligned: !!ownProps.isCenteredAligned,
    isOutlineStyle: !!ownProps.isOutlineStyle,
    isOrangeBackground: !!ownProps.isOrangeBackground,
    isSocialPayButtonsFirst: !!ownProps.isSocialPayButtonsFirst
  }: PropsType)
}

const mapDispatchToProps = (dispatch: Dispatch<any>): any => {
  return ({
    signIn: (email: string, password: string): void =>
      dispatch(signIn(email, password)),
    signUp: (email: string, password: string): void =>
      dispatch(signUp(email, password))
  }: DispatchPropsType)
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AuthFormContainer)
)
